import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { PiggyBank, Loader2, RotateCcw, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const WINDOWS = [
  { value: "3m", label: "Últimos 3 meses" },
  { value: "year", label: "Este año" },
];

function BudgetRow({ item, onSave, onReset }) {
  const { format } = useCurrency();
  const [val, setVal] = useState(String(item.budget));
  useEffect(() => { setVal(String(item.budget)); }, [item.budget]);
  const pct = Math.min(item.progress, 100);
  const over = item.spent > item.budget && item.budget > 0;

  const commit = () => {
    const num = Number(val);
    if (isNaN(num) || num < 0) { setVal(String(item.budget)); return; }
    if (num !== item.budget) onSave(item.category, num);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-4" data-testid={`budget-row-${item.category}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-medium truncate">{item.category}</p>
          <p className="text-xs text-muted-foreground">
            Promedio <span className="font-numeric">{format(item.average)}</span>
            {item.is_override && <span className="ml-1 text-emerald-500">· ajustado</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-numeric">$</span>
            <Input data-testid={`budget-input-${item.category}`} type="number" step="0.01" value={val}
              onChange={(e) => setVal(e.target.value)} onBlur={commit}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              className="w-28 h-9 pl-6 rounded-lg font-numeric text-right" />
          </div>
          {item.is_override && (
            <button onClick={() => onReset(item.category)} data-testid={`budget-reset-${item.category}`}
              title="Volver al promedio" className="w-9 h-9 rounded-lg border border-border hover:bg-accent flex items-center justify-center text-muted-foreground">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full transition-all ${over ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-muted-foreground">Gastado <span className="font-numeric text-foreground">{format(item.spent)}</span></span>
        <span className={`font-numeric ${over ? "text-rose-500" : "text-muted-foreground"}`}>
          {over ? `Excedido ${format(Math.abs(item.remaining))}` : `Disponible ${format(item.remaining)}`}
        </span>
      </div>
      {item.history && item.history.some((h) => h.value > 0) && (
        <div className="mt-3 pt-3 border-t border-border/60">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1.5">Gasto real · últimos 6 meses</p>
          <div className="flex items-end gap-1.5" data-testid={`budget-history-${item.category}`}>
            {(() => {
              const max = Math.max(...item.history.map((h) => h.value), 1);
              return item.history.map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={format(h.value)}>
                  <div className="w-full flex items-end justify-center" style={{ height: 34 }}>
                    <div className="w-full rounded-t-sm bg-rose-500/60" style={{ height: `${Math.max((h.value / max) * 34, h.value > 0 ? 3 : 0)}px` }} />
                  </div>
                  <span className="text-[9px] text-muted-foreground">{h.month}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Budget() {
  const qc = useQueryClient();
  const { format } = useCurrency();
  const [window, setWindow] = useState("3m");
  const { data, isLoading } = useQuery({
    queryKey: ["budget", window],
    queryFn: async () => (await api.get(`/budget?window=${window}`)).data,
  });

  const save = async (category, amount) => {
    try { await api.post("/budget", { category, amount }); toast.success("Presupuesto ajustado"); qc.invalidateQueries({ queryKey: ["budget"] }); }
    catch { toast.error("No se pudo guardar"); }
  };
  const reset = async (category) => {
    try { await api.post("/budget/reset", { category }); toast.success("Vuelto al promedio"); qc.invalidateQueries({ queryKey: ["budget"] }); }
    catch { toast.error("Error"); }
  };

  const totalPct = data && data.total_budget > 0 ? Math.min((data.total_spent / data.total_budget) * 100, 100) : 0;
  const totalOver = data && data.total_spent > data.total_budget && data.total_budget > 0;

  return (
    <div className="py-4 max-w-3xl" data-testid="budget-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><PiggyBank className="w-6 h-6 text-emerald-500" /><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Presupuesto</h1></div>
          <p className="text-muted-foreground text-sm mt-1">Promedio de gasto por concepto. Ajústalo a tu medida.</p>
        </div>
        <div className="flex rounded-xl border border-border p-1 bg-secondary/40">
          {WINDOWS.map((w) => (
            <button key={w.value} data-testid={`budget-window-${w.value}`} onClick={() => setWindow(w.value)}
              className={`px-3 py-1.5 rounded-lg text-sm ${window === w.value ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{w.label}</button>
          ))}
        </div>
      </div>

      {isLoading || !data ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/40 p-6 my-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Presupuesto del mes</p>
                <p className="font-numeric font-bold text-3xl mt-1">{format(data.total_budget)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end"><TrendingDown className="w-3.5 h-3.5" /> Gastado</p>
                <p className={`font-numeric font-bold text-2xl mt-1 ${totalOver ? "text-rose-500" : "text-emerald-500"}`}>{format(data.total_spent)}</p>
              </div>
            </div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden mt-4">
              <div className={`h-full rounded-full ${totalOver ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${totalPct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Promedio base: {data.window === "year" ? "año en curso" : "últimos 3 meses"} · {data.months} mes(es)</p>
          </div>

          <div className="space-y-3" data-testid="budget-list">
            {data.items.map((it) => <BudgetRow key={it.category} item={it} onSave={save} onReset={reset} />)}
            {data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aún no hay conceptos de gasto. Registra movimientos «To».</p>}
          </div>
        </>
      )}
    </div>
  );
}
